// src/components/JoinView.ts
// Household invitation acceptance screen

import type { AppState, Household } from '../types';
import { api } from '../services/api';
import { showToast } from './Toast';

export class JoinView {
  private el: HTMLElement;
  private inviteInfo: any = null;
  private loading = true;
  private errorMsg: string | null = null;

  constructor(
    private state: AppState,
    private code: string,
    private callbacks: {
      onJoined: (household: Household) => void;
      onCancel: () => void;
      onRequestLogin: () => void;
    }
  ) {
    this.el = document.createElement('div');
    this.el.className = 'main-content';
    this.loadInviteInfo();
  }

  public getElement(): HTMLElement {
    return this.el;
  }

  public update(state: AppState) {
    this.state = state;
    this.render();
  }

  private async loadInviteInfo() {
    this.loading = true;
    this.errorMsg = null;
    this.render();

    try {
      this.inviteInfo = await api.getInviteInfo(this.code);
      this.loading = false;
      this.render();
    } catch (err: any) {
      this.loading = false;
      this.errorMsg = err.message || 'Invalid or expired invite';
      this.render();
    }
  }

  private render() {
    if (this.loading) {
      this.el.innerHTML = `
        <div style="text-align:center;padding:3rem 1rem;">
          <div class="sync-spinner" style="width:28px;height:28px;border-color:var(--sage);border-top-color:transparent;margin:0 auto 1rem;"></div>
          <div style="font-size:1rem;color:var(--ink-secondary);">Checking household invite…</div>
        </div>
      `;
      return;
    }

    if (this.errorMsg) {
      this.el.innerHTML = `
        <div style="max-width:440px;margin:2rem auto;background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-lg);padding:2rem;text-align:center;box-shadow:var(--shadow-md);">
          <div style="font-size:2.5rem;margin-bottom:0.75rem;">⚠️</div>
          <h2 style="font-size:1.4rem;font-family:var(--font-serif);margin-bottom:0.5rem;">Invite Unavailable</h2>
          <p style="color:var(--ink-secondary);font-size:0.9rem;margin-bottom:1.5rem;">${escapeHtml(this.errorMsg)}</p>
          <button class="btn btn-secondary" id="join-back-btn" style="width:100%;">Return to Home</button>
        </div>
      `;
      this.el.querySelector('#join-back-btn')?.addEventListener('click', () => this.callbacks.onCancel());
      return;
    }

    const { householdName, role, inviterName } = this.inviteInfo;
    const { authenticated, user } = this.state;

    const actionSection = authenticated
      ? `
        <div style="margin-top:1.5rem;display:flex;flex-direction:column;gap:0.75rem;">
          <button class="btn btn-sage" id="accept-join-btn" style="width:100%;padding:0.75rem;font-size:1rem;font-weight:600;">
            ✓ Accept Invite & Join
          </button>
          <button class="btn btn-ghost" id="decline-join-btn" style="width:100%;">
            Decline
          </button>
        </div>
      `
      : `
        <div style="margin-top:1.5rem;background:var(--bg-subtle);padding:1rem;border-radius:var(--radius-md);text-align:center;">
          <p style="font-size:0.85rem;color:var(--ink-secondary);margin-bottom:0.75rem;">
            Please sign in with your Google account to accept this invitation.
          </p>
          <button class="btn btn-primary" id="join-signin-btn" style="width:100%;padding:0.7rem;font-weight:600;">
            Sign In to Join
          </button>
        </div>
      `;

    this.el.innerHTML = `
      <div style="max-width:440px;margin:2rem auto;background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-lg);padding:2rem;text-align:center;box-shadow:var(--shadow-md);">
        <div style="font-size:2.8rem;margin-bottom:0.5rem;">🏡</div>
        <div style="font-size:0.8rem;text-transform:uppercase;letter-spacing:0.1em;color:var(--ink-muted);font-weight:700;margin-bottom:0.25rem;">
          Household Invitation
        </div>
        <h2 style="font-size:1.6rem;font-family:var(--font-serif);color:var(--ink-primary);margin-bottom:0.4rem;">
          ${escapeHtml(householdName)}
        </h2>
        <p style="color:var(--ink-secondary);font-size:0.92rem;line-height:1.5;">
          <strong>${escapeHtml(inviterName)}</strong> has invited you to join this household as a <span class="role-badge ${role}">${role}</span>.
        </p>

        ${actionSection}
      </div>
    `;

    this.el.querySelector('#accept-join-btn')?.addEventListener('click', async () => {
      try {
        const res = await api.joinHousehold(this.code);
        showToast(`Joined ${householdName}!`);
        this.callbacks.onJoined(res.household);
      } catch (err: any) {
        showToast('Failed to join: ' + err.message);
      }
    });

    this.el.querySelector('#decline-join-btn')?.addEventListener('click', () => this.callbacks.onCancel());
    this.el.querySelector('#join-signin-btn')?.addEventListener('click', () => this.callbacks.onRequestLogin());
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
